import type { TFunction } from 'i18next';
import type { StrategicEpic } from '../../../api/tasks-client/task-client.type';
import { useStrategicEpics } from '../../../api/tasks-client/tasks-client.hooks';
import type { StrategyLevelConfig } from '../strategy-config.type';
import { EpicsFilters } from './epics-filters';
import { EpicsMainSection } from './epics-main-section';
import { EpicsOverview } from './epics-overview';

/**
 * Build the Epics strategy level configuration against the caller's
 * translation function so org-level text_config overrides apply (the prior
 * implementation imported the i18n singleton and used getters; this factory
 * shape lets the caller pass `useTranslation().t` and get a fresh config
 * each render with no singleton dependency).
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {StrategyLevelConfig<StrategicEpic>} Strategy-level config bound to `t`
 */
export const getEpicsConfig = (t: TFunction): StrategyLevelConfig<StrategicEpic> => ({
  level: 'epics',
  tabId: 'epics',

  labels: {
    pageTitle: `${t('common.epics')} - Bloomfilter`,
    overviewTitle: `${t('common.epics')} Overview`,
    filtersTitle: 'Filters',
    emptyLabel: `${t('common.epics')} not found`,
    byStatusTitle: `${t('common.epics')} by Status`,
    byStateTitle: `${t('common.epics')} by State`,
    searchPlaceholder: `Search ${t('common.epics')}...`,
  },

  features: {
    hasTreeMapView: false,
    hasCreatePresentation: false,
    hasExport: true,
    hasSearch: true,
    hasAddNew: false,
    hasFilters: true,
    hasSidePanel: true,
  },

  hooks: {
    useItems: () => {
      const { epics, query: epicsQuery } = useStrategicEpics();
      return { items: epics ?? [], query: epicsQuery };
    },
  },

  components: {
    Filters: EpicsFilters,
    Overview: EpicsOverview,
    MainSection: EpicsMainSection,
  },
});
